import api from 'api'
import { ImpressaoAtendimentoInput } from 'api/ImpressaoAtendimentoIndividual'
import { modal } from 'bold-ui'
import { useAlert } from 'components/alert'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import useSession from 'components/auth/useSession'
import {
  getFirstGraphqlError,
  handleError,
  handleValidationError,
  isValidationError,
  useErrorHandler,
} from 'components/error'
import { Form, FormRenderProps } from 'components/form'
import changeValue from 'components/form/mutators/changeValue'
import { confirm } from 'components/modals/confirm'
import { ValidationErrorsModal } from 'components/modals/fields-summary'
import { useFlags } from 'config/useFlagsContext'
import { parseISO, subDays, subMilliseconds, subMonths } from 'date-fns'
import { Decorator, FormApi, FormState, FormSubscription } from 'final-form'
import arrayMutators from 'final-form-arrays'
import { useApolloClient } from 'graphql/hooks'
import {
  useCancelarAtendimentoMutation,
  useMedicoesAtendimentosAnterioresQuery,
  useProblemasFaltantesLpcQuery,
  useRetificarAtendimentoMutation,
  useSalvarAtendimentoIndividualMutation,
  useSalvarAtendimentoIndividualParaAprovacaoMutation,
} from 'graphql/hooks.generated'
import {
  AcessoCbo,
  AtendimentoIndividualInput,
  InativarAtendimentoInput,
  TipoAtendimentoProfissional,
  TipoEstabelecimentoEnum,
  TipoGravidezEnum,
} from 'graphql/types.generated'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { reportFormatDate, useHandleImpressao } from 'hooks/impressao/useHandleImpressao'
import { useAlertasSdc } from 'hooks/sdc/useAlertasSdc'
import { useServerTime } from 'hooks/useServerTime'
import React, { Dispatch, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router'
import { isBetween } from 'util/date/compare'
import { toDate } from 'util/date/formatDate'
import { ErrorObject } from 'util/validation'
import { podeFinalizarAtendimentoObservacao } from 'view/atendimentos/atendimento-individual/atendimento-observacao/util-atendObservacao'
import {
  CidadaoCalculator,
  createAtendimentoIndividualCalculator,
  PermissionsCalculator,
} from 'view/atendimentos/atendimento-individual/calculator-atendimentoIndividual'
import { convertAtendimentoIndividualModelToInput } from 'view/atendimentos/atendimento-individual/converter'
import { logEventCancelarAtendimento } from 'view/atendimentos/atendimento-individual/logCancelarAtendimento'
import { logEventFinalizarAtendimento } from 'view/atendimentos/atendimento-individual/logFinalizarAtendimento'
import {
  CiapCidPreNatal,
  meta,
  RetificacaoAtendimentoState,
  SoapState,
  TipoServicoModel,
} from 'view/atendimentos/atendimento-individual/model'
import { atendimentoIndividualValidator } from 'view/atendimentos/atendimento-individual/validator-atendimentoIndividual'
import { validateCpfCns } from 'view/atendimentos/atendimento-vacinacao/validator-atendimentoVacinacao'
import { ProcedimentoAutomatico, RegistroAvaliacaoPuericultura } from 'view/atendimentos/model-atendimento'
import { AgendamentoDia } from 'view/atendimentos/types/AgendamentoDia'
import { AtendimentoProfissional } from 'view/atendimentos/types/AtendimentoProfissionalModel'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/model-historicoMedicao'
import { useJustificativaVerProntuarioId } from 'view/prontuario/justificativa/useJustificativaVerProntuarioId'
import { REGISTRO_TARDIO_PATH } from 'view/registro-tardio/RegistroTardioRootView'
import { retificacaoAtendimentoConfirmModal } from 'view/retificacao-atendimento/retificacaoAtendimentoConfirmModal'
import { useRetificacaoStorage } from 'view/retificacao-atendimento/useJustificativaRetificacaoStorage'

import { useVerificarAgendamentosConflitantes } from '../../../agenda/hooks/useVerificarAgendamentosConflitantes'
import { atendimentoFooterMessage } from '../atendimentoFooterMessage'
import { AtendimentoIndividualFooter } from '../AtendimentoIndividualFooter'
import {
  convertMedicaoAnterior,
  convertMedicaoOutrosAtendimentos,
  convertMedicoesNoNascimento,
} from '../components/modals/medicoes/converter-medicoes'
import { PeriodoGestacaoModel } from '../components/modals/types/PeriodoGestacaoModel'
import { createVacinacaoErrorModal } from '../vacinacao/VacinacaoCalendarioView'
import { Problema } from './aside/types/ProblemaModel'
import { hasProblemaComCiapW78Ativo } from './avaliacao/components/problemas-condicoes/utils/verifications-preNatal'
import { dirtyFieldsModalConfig, soapValidationErrorModalConfig } from './fields-summary-modal/soapFieldModalConfigs'
import { soapFieldPaths, soapFormLabels } from './fields-summary-modal/soapFormLabels'
import { convertSoapErrorsFromBackend } from './fields-summary-modal/validation-errors-modal/converter-soapErrorsFromBackend'
import { convertAgendarConsultasToAgendamentosConflitantesInput } from './finalizacao'
import { FormAtivoObjetivoEnum } from './objetivo/components/SwitchButtonObjetivoForm'
import { grupoCboProcedimentosAutomaticos } from './plano/acessos'
import { HasAccessProcedimentos } from './plano/calculator-plano-medicoes'
import { SoapPage } from './SoapPage'
import { SoapRootView } from './SoapRootView'
import { handleKeyDown } from './util'

const subscription: FormSubscription = {} /* evita problemas de performance */

const createDecorators = (
  apollo,
  procedimentosAutomaticos: ProcedimentoAutomatico[],
  registroAvaliacaoPuericultura: RegistroAvaliacaoPuericultura,
  cbo2002: string,
  tipoAtendimentoProfissional: TipoAtendimentoProfissional,
  prontuarioId: ID,
  ciapCidPreNatal: CiapCidPreNatal,
  dataAtendimento: Instant,
  cidadao: CidadaoCalculator,
  tipoEstabelecimento: TipoEstabelecimentoEnum,
  permissions: PermissionsCalculator,
  ultimaDumPreNatalAtivo: LocalDate,
  isRegistroTardio: boolean,
  isObservacaoAndResponsavel: boolean,
  hasProblemaComCiapW78AtivoPersistido: boolean,
  isObservacaoAndPreNatal: boolean,
  flagDengue: boolean
): Decorator[] => [
  createAtendimentoIndividualCalculator({
    apollo,
    procedimentosAutomaticos,
    registroAvaliacaoPuericultura,
    cbo2002,
    tipoAtendimentoProfissional,
    prontuarioId,
    ciapCidPreNatal,
    dataAtendimento,
    cidadao,
    tipoEstabelecimento,
    permissions,
    ultimaDumPreNatalAtivo,
    isRegistroTardio,
    isObservacaoAndResponsavel,
    hasProblemaComCiapW78AtivoPersistido,
    flagDengue,
    isObservacaoAndPreNatal,
  }),
]

export interface SoapViewProps {
  atendimentoId: ID
  prontuarioId: ID
  cidadao: CidadaoAtendimento
  isRegistroTardio: boolean
  tiposServico: TipoServicoModel[]
  dataAtendimento: Instant
  procedimentosAutomaticos?: ProcedimentoAutomatico[]
  registroAvaliacaoPuericultura: RegistroAvaliacaoPuericultura
  cbo2002: string
  agendamentoAtendimentoId: ID
  concluiAgendamento: boolean
  hasEncaminhamentoExterno: boolean
  agendamentosDia: AgendamentoDia[]
  atendimentoProfissional: AtendimentoProfissional
  nomeAtendimento: string
  ciapCidPreNatal: CiapCidPreNatal
  cacheState: SoapState
  dirtyInitialValues: SoapState
  gestacoes: PeriodoGestacaoModel[]
  ultimaDumPreNatalAtivo: LocalDate
  problemasAtivosELatentes?: Problema[]
  problemasResolvidos?: Problema[]
  updateCache: Dispatch<SoapState>
  clearCache(updateState?: boolean): void
  headerRef: React.RefObject<HTMLDivElement>
  qtdTotalProcedimentos: number
  preNatalAtivoHasTipoGravidez: boolean
  hasIvcfPreenchidoEmAtendObservacaoAnterior: boolean
}

export function SoapView(props: SoapViewProps) {
  const { cacheState, dirtyInitialValues } = props
  const [initialValues] = useState(cacheState) // usado pra setar os valores iniciais do Form apenas na primeira vez que renderizar
  return (
    <div>
      <SoapForm {...props} dirtyInitialValues={dirtyInitialValues} initialValues={initialValues} />
    </div>
  )
}

export interface SoapFormProps extends SoapViewProps {
  initialValues: SoapState
}

const SoapForm = memo((props: SoapFormProps) => {
  const {
    atendimentoId,
    prontuarioId,
    isRegistroTardio,
    agendamentosDia,
    dataAtendimento,
    procedimentosAutomaticos,
    registroAvaliacaoPuericultura,
    initialValues,
    dirtyInitialValues,
    agendamentoAtendimentoId,
    concluiAgendamento,
    cbo2002,
    hasEncaminhamentoExterno,
    cidadao,
    atendimentoProfissional,
    updateCache,
    clearCache,
    nomeAtendimento,
    ciapCidPreNatal,
    problemasAtivosELatentes,
    problemasResolvidos,
    gestacoes,
    ultimaDumPreNatalAtivo,
    headerRef,
    qtdTotalProcedimentos,
    preNatalAtivoHasTipoGravidez,
    hasIvcfPreenchidoEmAtendObservacaoAnterior,
  } = props

  const history = useHistory()
  const [finalizar] = useSalvarAtendimentoIndividualMutation()
  const [retificar] = useRetificarAtendimentoMutation()
  const [enviarParaAprovacao] = useSalvarAtendimentoIndividualParaAprovacaoMutation()
  const [cancelar, { loading: isLoadingCancelar }] = useCancelarAtendimentoMutation()
  const { verificarAgendamentosConflitantes } = useVerificarAgendamentosConflitantes()
  const handleRejection = useErrorHandler()
  const { data, isEstagio } = useSession()
  const apollo = useApolloClient()
  const dataNascimentoCidadao = Number(parseISO(cidadao?.dataNascimento))
  const { getServerTimeNow } = useServerTime()
  const dataReferencia = atendimentoProfissional?.iniciadoEm
  const tipoAtendProf = atendimentoProfissional?.tipo
  const { analytics } = useFirebase()
  const { getRetificacaoStorageValue, deleteStorageRetificacao } = useRetificacaoStorage(atendimentoId)
  const flags = useFlags()
  const { callbackUrl: retificacaoCallbackUrl, justificativaVerProntuarioId } = getRetificacaoStorageValue()
  const { handleImpressaoPath } = useHandleImpressao()

  const {
    data: { problemasFaltantesLPC: problemasObrigatoriosAdicionarLPC },
  } = useProblemasFaltantesLpcQuery({
    variables: { prontuarioId: prontuarioId },
    skip:
      !flags.PILOTO_DIABETES_HIPERTENSAO_LPC || tipoAtendProf === TipoAtendimentoProfissional.ATENDIMENTO_PROCEDIMENTOS,
  })

  const {
    cidadao: { isGestante },
    permissoes: { hasPermissionPreNatal, hasPermissionAcompanhamentoPreNatal, somenteCiap },
    observacao: { isAtendimentoObservacao, isObservacaoAndResponsavel },
    tiposAtendimento: { isAtendimentoOdonto },
    statusRevisaoAtendimento,
    isRetificacao,
  } = useAtendimentoContext()

  const cidadaoBaseUrl = `/cidadao/${cidadao.id}`
  const [, setJustificativaVerProntuarioId] = useJustificativaVerProntuarioId(cidadaoBaseUrl, cidadao.id)

  const { hasCboAuth, tipoEstabelecimento } = useSession({ fetchPolicy: 'cache-only' })
  const { acesso } = useAcessoLotacaoOrEstagio()
  const podeFinalizarObservacao = podeFinalizarAtendimentoObservacao(acesso, tipoEstabelecimento)
  const isObservacaoAndPreNatal =
    isAtendimentoObservacao &&
    initialValues.objetivo.atendimentosEspecificos.formAtivo === FormAtivoObjetivoEnum.PRE_NATAL

  const callbackUrl = isRetificacao
    ? retificacaoCallbackUrl
    : isRegistroTardio
    ? REGISTRO_TARDIO_PATH
    : '/lista-atendimento'

  const hasAccessProcedimentos: HasAccessProcedimentos = useMemo(
    () => ({
      desenvolvimentoCrianca: hasCboAuth(grupoCboProcedimentosAutomaticos.desenvolvimentoCrianca),
      crescimentoCrianca: hasCboAuth(grupoCboProcedimentosAutomaticos.crescimentoCrianca),
      glicemiaCapilar: hasCboAuth(grupoCboProcedimentosAutomaticos.glicemiaCapilar),
    }),
    [hasCboAuth]
  )
  const isMedicoOuEnfermeiro = useMemo(() => hasCboAuth([AcessoCbo.MEDICO, AcessoCbo.ENFERMEIRO]), [hasCboAuth])
  const hasProblemaComCiapW78AtivoPersistido = hasProblemaComCiapW78Ativo(problemasAtivosELatentes)

  const decorators = useMemo(
    () =>
      createDecorators(
        apollo,
        procedimentosAutomaticos,
        registroAvaliacaoPuericultura,
        cbo2002,
        tipoAtendProf,
        prontuarioId,
        ciapCidPreNatal,
        dataAtendimento,
        {
          dataNascimento: cidadao.dataNascimento,
          problemasAtivosELatentes,
          isGestante,
          idadeEmAnos: cidadao.idadeEmAnos,
          desejaInformarIdentidadeGenero: cidadao.desejaInformarIdentidadeGenero,
          sexo: cidadao.sexo,
        },
        tipoEstabelecimento,
        { hasAccessProcedimentos, hasPermissionPreNatal, hasPermissionAcompanhamentoPreNatal },
        ultimaDumPreNatalAtivo,
        isRegistroTardio,
        isObservacaoAndResponsavel,
        hasProblemaComCiapW78AtivoPersistido,
        isObservacaoAndPreNatal,
        flags.PILOTO_ESTRUTURA_GRUPO_DENGUE
      ),
    [
      apollo,
      procedimentosAutomaticos,
      registroAvaliacaoPuericultura,
      cbo2002,
      tipoAtendProf,
      prontuarioId,
      ciapCidPreNatal,
      dataAtendimento,
      cidadao.dataNascimento,
      cidadao.idadeEmAnos,
      cidadao.desejaInformarIdentidadeGenero,
      cidadao.sexo,
      problemasAtivosELatentes,
      isGestante,
      tipoEstabelecimento,
      hasAccessProcedimentos,
      hasPermissionPreNatal,
      hasPermissionAcompanhamentoPreNatal,
      ultimaDumPreNatalAtivo,
      isRegistroTardio,
      isObservacaoAndResponsavel,
      hasProblemaComCiapW78AtivoPersistido,
      isObservacaoAndPreNatal,
      flags.PILOTO_ESTRUTURA_GRUPO_DENGUE,
    ]
  )
  const validators = useMemo(
    () =>
      atendimentoIndividualValidator(
        { ...cidadao, isGestante },
        dataAtendimento,
        tipoAtendProf,
        problemasAtivosELatentes,
        !concluiAgendamento,
        hasEncaminhamentoExterno,
        hasPermissionPreNatal,
        isAtendimentoObservacao,
        isObservacaoAndResponsavel,
        podeFinalizarObservacao,
        getServerTimeNow,
        qtdTotalProcedimentos,
        preNatalAtivoHasTipoGravidez,
        hasProblemaComCiapW78AtivoPersistido,
        hasIvcfPreenchidoEmAtendObservacaoAnterior,
        problemasObrigatoriosAdicionarLPC,
        isMedicoOuEnfermeiro
      ),
    [
      cidadao,
      isGestante,
      dataAtendimento,
      tipoAtendProf,
      problemasAtivosELatentes,
      concluiAgendamento,
      hasEncaminhamentoExterno,
      hasPermissionPreNatal,
      isAtendimentoObservacao,
      isObservacaoAndResponsavel,
      podeFinalizarObservacao,
      getServerTimeNow,
      qtdTotalProcedimentos,
      preNatalAtivoHasTipoGravidez,
      hasProblemaComCiapW78AtivoPersistido,
      hasIvcfPreenchidoEmAtendObservacaoAnterior,
      problemasObrigatoriosAdicionarLPC,
      isMedicoOuEnfermeiro,
    ]
  )

  const alert = useAlert()

  const nomeCompletoAtendimento = `Atendimento ${nomeAtendimento}`

  const printItem = useCallback(
    (itemToPrint: ImpressaoAtendimentoInput) => {
      handleImpressaoPath({
        apiPath: api.atendimentoIndividual.imprimirAtendimentoIndividual,
        values: itemToPrint,
        file: `AtendimentoIndividual.pdf`,
        dateFormat: reportFormatDate,
      })
    },
    [handleImpressaoPath]
  )

  const { setSdcAtendimentoValues } = useAlertasSdc()

  const [formInitialized, setFormInitialized] = useState(false)

  const { data: dataMedicoes } = useMedicoesAtendimentosAnterioresQuery({
    variables: { prontuarioId },
    skip: isRetificacao,
  })

  useEffect(() => {
    setFormInitialized(true)
    const cidadaoIdade = cidadao.idadeEmAnos
    const problemasCondicoesLpc = initialValues?.problemasECondicoes || []
    const problemasCondicoesAvaliacao = initialValues?.avaliacao?.problemasECondicoes || []
    const problemasCondicoes = [...problemasCondicoesLpc, ...problemasCondicoesAvaliacao]
    const formAtivoObjetivo = initialValues?.objetivo.atendimentosEspecificos?.formAtivo
    const hoje = new Date()
    hoje.setUTCHours(0, 0, 0, 0)
    const medicoesAnteriores: HistoricoMedicaoModel[] = [
      ...(dataMedicoes?.medicoes ?? [])
        .sort((a, b) => b.dataMedicao - a.dataMedicao)
        .map(convertMedicaoOutrosAtendimentos),
      ...(dataMedicoes?.medicoesNoNascimento
        ? [convertMedicoesNoNascimento(dataMedicoes?.medicoesNoNascimento, cidadao.dataNascimento)]
        : []),
    ]
    const pesoMedicoesAnteriores =
      [...(initialValues?.medicoesAnteriores?.map(convertMedicaoAnterior) || []), ...medicoesAnteriores]
        ?.filter((m) => {
          const dataMedicao = toDate(m.dataMedicao)

          const dataLimite = subMilliseconds(cidadaoIdade >= 13 ? subMonths(hoje, 12) : subDays(hoje, 1), 1)

          return !!m.valorPeso && isBetween(dataMedicao, dataLimite, hoje)
        })
        .sort((a, b) => (b.dataMedicao > a.dataMedicao ? 1 : -1))?.[0]
        ?.valorPeso.toString() || null
    const peso = initialValues?.objetivo?.medicoes?.peso || pesoMedicoesAnteriores
    setSdcAtendimentoValues({
      problemasCondicoes: problemasCondicoes,
      prontuarioId: prontuarioId,
      atendProfData: toDate(atendimentoProfissional.iniciadoEm),
      pesoCidadao: peso,
      isRegistroTardio: isRegistroTardio,
      isGravidezMultipla: [TipoGravidezEnum.DUPLA_GEMELAR, TipoGravidezEnum.TRIPLA_OU_MAIS].includes(
        initialValues?.preNatal?.tipoGravidez?.value
      ),
      isBotaoPreNatalAtivo: formAtivoObjetivo === FormAtivoObjetivoEnum.PRE_NATAL,
    })
  }, [
    atendimentoProfissional.iniciadoEm,
    initialValues,
    prontuarioId,
    setSdcAtendimentoValues,
    isRegistroTardio,
    cidadao.idadeEmAnos,
    cidadao.dataNascimento,
    dataMedicoes,
  ])

  const handleFormChange = useCallback(
    (values: SoapState) => {
      /* Avoids the error of updating a component while rendering a different component. */
      if (formInitialized) {
        values.lastSaved = new Date()
        const cidadaoIdade = cidadao.idadeEmAnos
        const problemasCondicoesLpc = values?.problemasECondicoes || []
        const problemasCondicoesAvaliacao = values?.avaliacao?.problemasECondicoes || []
        const problemasCondicoes = [...problemasCondicoesLpc, ...problemasCondicoesAvaliacao]
        const formAtivoObjetivo = values?.objetivo.atendimentosEspecificos?.formAtivo
        const hoje = new Date()
        hoje.setUTCHours(0, 0, 0, 0)
        const medicoesAnteriores: HistoricoMedicaoModel[] = [
          ...(dataMedicoes?.medicoes ?? [])
            .sort((a, b) => b.dataMedicao - a.dataMedicao)
            .map(convertMedicaoOutrosAtendimentos),
          ...(dataMedicoes?.medicoesNoNascimento
            ? [convertMedicoesNoNascimento(dataMedicoes?.medicoesNoNascimento, cidadao.dataNascimento)]
            : []),
        ]
        const pesoMedicoesAnteriores =
          [...(values?.medicoesAnteriores?.map(convertMedicaoAnterior) || []), ...medicoesAnteriores]
            ?.filter((m) => {
              const dataMedicao = toDate(m.dataMedicao)

              const dataLimite = subMilliseconds(cidadaoIdade >= 13 ? subMonths(hoje, 12) : subDays(hoje, 1), 1)

              return !!m.valorPeso && isBetween(dataMedicao, dataLimite, hoje)
            })
            .sort((a, b) => (b.dataMedicao > a.dataMedicao ? 1 : -1))?.[0]
            ?.valorPeso.toString() || null
        const peso = values?.objetivo?.medicoes?.peso || pesoMedicoesAnteriores
        setSdcAtendimentoValues({
          problemasCondicoes: problemasCondicoes,
          prontuarioId: prontuarioId,
          atendProfData: toDate(atendimentoProfissional.iniciadoEm),
          pesoCidadao: peso,
          isRegistroTardio: isRegistroTardio,
          isGravidezMultipla: [TipoGravidezEnum.DUPLA_GEMELAR, TipoGravidezEnum.TRIPLA_OU_MAIS].includes(
            values?.preNatal?.tipoGravidez?.value
          ),
          isBotaoPreNatalAtivo: formAtivoObjetivo === FormAtivoObjetivoEnum.PRE_NATAL,
        })
      }
      updateCache(values)
    },
    [
      formInitialized,
      updateCache,
      cidadao.idadeEmAnos,
      cidadao.dataNascimento,
      dataMedicoes,
      setSdcAtendimentoValues,
      prontuarioId,
      atendimentoProfissional.iniciadoEm,
      isRegistroTardio,
    ]
  )

  const footerMessages = useMemo(
    () => atendimentoFooterMessage(tipoAtendProf, isAtendimentoObservacao, isObservacaoAndResponsavel, isRetificacao),
    [isAtendimentoObservacao, isObservacaoAndResponsavel, isRetificacao, tipoAtendProf]
  )

  const goToCallbackUrl = useCallback(() => {
    justificativaVerProntuarioId && setJustificativaVerProntuarioId(justificativaVerProntuarioId)
    isRetificacao && deleteStorageRetificacao()

    // Necessário setTimeout para só navegar depois de atualizar os storages
    // TODO (RNG) #24441: Criar interface async para os "useStorage"
    clearCache(false)
    setTimeout(() => history.push(callbackUrl))
  }, [
    callbackUrl,
    clearCache,
    deleteStorageRetificacao,
    history,
    isRetificacao,
    justificativaVerProntuarioId,
    setJustificativaVerProntuarioId,
  ])

  const cancelarAtendimento = useCallback(
    (isStartObservacao) => async () => {
      try {
        await cancelar({ variables: { atendimentoId } })

        logEventCancelarAtendimento(
          analytics.logEvent,
          nomeCompletoAtendimento,
          isStartObservacao,
          isAtendimentoObservacao,
          atendimentoProfissional.finalizadoEm
        )

        goToCallbackUrl()
      } catch (e) {
        return handleRejection(e as any)
      }
    },
    [
      cancelar,
      atendimentoId,
      analytics.logEvent,
      nomeCompletoAtendimento,
      isAtendimentoObservacao,
      atendimentoProfissional.finalizadoEm,
      goToCallbackUrl,
      handleRejection,
    ]
  )

  const cancelarRetificacao = useCallback(() => {
    //TODO(RNG) #24375: Log de cancelamento de retificacao
    goToCallbackUrl()
  }, [goToCallbackUrl])

  const handleCancelar = useCallback(
    (isStartObservacao: boolean) => {
      confirm({
        title: footerMessages.confirmTitle,
        body: footerMessages.body,
        confirmLabel: 'Sim, cancelar',
        cancelLabel: 'Não, continuar aqui',
        onConfirm: !isRetificacao ? cancelarAtendimento(isStartObservacao) : cancelarRetificacao,
      })()
    },
    [footerMessages.confirmTitle, footerMessages.body, isRetificacao, cancelarAtendimento, cancelarRetificacao]
  )

  const handleClickAtualizarCadastroCidadao = useCallback(
    () =>
      history.push(`${cidadaoBaseUrl}/edit?callbackUrl=lista-atendimento/atendimento&callbackParams=${atendimentoId}`),
    [atendimentoId, cidadaoBaseUrl, history]
  )

  const handleRetificar = useCallback(
    async (
      retificacaoAtendimento: RetificacaoAtendimentoState,
      atendimentoRetificadoInput: AtendimentoIndividualInput
    ) => {
      const inativarAtendimentoInput: InativarAtendimentoInput = {
        atendimentoProfissionalId: atendimentoProfissional.id,
        justificativa: retificacaoAtendimento?.justificativa,
        historicoCabecalhoId: retificacaoAtendimento?.historicoCabecalhoId,
      }

      const retificarResponse = await retificar({
        variables: {
          input: {
            atendimentoRetificadoInput,
            inativarAtendimentoInput,
          },
        },
      })

      atendimentoRetificadoInput.imprimirAtendimento &&
        printItem({
          atendimentoProfissionalId: retificarResponse.data.retificarAtendimento,
        })
      //TODO(RNG) #24375: Log de finalização do atendimento retificado
      alert('success', `${nomeCompletoAtendimento} retificado com sucesso.`)

      goToCallbackUrl()

      return retificarResponse
    },
    [atendimentoProfissional.id, retificar, printItem, alert, nomeCompletoAtendimento, goToCallbackUrl]
  )

  const handleFinalizar = useCallback(
    async (values: SoapState, formApi: FormApi<SoapState>) => {
      const errorCpfCnsRequired =
        values.vacinacao?.registroVacinacao?.length &&
        validateCpfCns(cidadao.cns || cidadao.cpf, handleClickAtualizarCadastroCidadao, false)

      if (errorCpfCnsRequired?.hasError) {
        createVacinacaoErrorModal(errorCpfCnsRequired)
      } else {
        const confirmouHorariosConflitantes = await verificarAgendamentosConflitantes(
          cidadao.id,
          convertAgendarConsultasToAgendamentosConflitantesInput(values.finalizacao?.agendamentoConsultas),
          cidadao.nomeSocial ?? cidadao.nome
        )

        if (!confirmouHorariosConflitantes) return

        const input = convertAtendimentoIndividualModelToInput(
          atendimentoId,
          values,
          dataNascimentoCidadao,
          dataReferencia,
          tipoAtendProf,
          isAtendimentoObservacao,
          isObservacaoAndResponsavel,
          isAtendimentoOdonto,
          flags
        )

        if (isEstagio) {
          const enviarAprovacaoResponse = await enviarParaAprovacao({
            variables: {
              input: {
                atendimentoInput: input,
                rascunho: JSON.stringify(values),
              },
            },
          })

          if (enviarAprovacaoResponse.data.salvarAtendimentoIndividualParaAprovacao) {
            analytics.logEvent('atendimento_enviado_revisao_EST', { tipo_de_atendimento: nomeCompletoAtendimento })
            alert('success', `${nomeCompletoAtendimento} enviado para aprovação com sucesso.`)
            goToCallbackUrl()
          }
        } else {
          try {
            if (!isRetificacao) {
              const finalizarResponse = await finalizar({
                variables: {
                  input,
                },
              })

              values.imprimirAtendimento &&
                printItem({
                  atendimentoProfissionalId: finalizarResponse.data.salvarAtendimentoIndividual.id,
                })

              logEventFinalizarAtendimento(
                analytics.logEvent,
                nomeCompletoAtendimento,
                values,
                cidadao.idadeEmAnos,
                isGestante,
                initialValues.antecedentes?.pessoal,
                isAtendimentoObservacao,
                statusRevisaoAtendimento
              )
              alert('success', `${nomeCompletoAtendimento} finalizado com sucesso.`)

              goToCallbackUrl()
            } else {
              const { initialValues, values } = formApi.getState()

              await retificacaoAtendimentoConfirmModal({
                initialValues,
                currentValues: values,
                nomeCidadao: (cidadao.nomeSocial ?? cidadao.nome).titleCase(),
                labels: soapFormLabels,
                config: dirtyFieldsModalConfig,
                meta,
                onRetificar: () => handleRetificar(values.retificacaoAtendimento, input),
                onRetificarSemAlteracoes: goToCallbackUrl,
              })
            }
          } catch (error) {
            const firstError = getFirstGraphqlError(error)
            const handledError: ErrorObject<AtendimentoIndividualInput> = handleError({
              error,
              suppressNotificationError: true,
            })

            if (isValidationError(firstError)) {
              modal({
                title: 'Para finalizar o atendimento é necessário corrigir o(s) registro(s):',
                icon: {
                  name: 'banOutline',
                  fill: 'danger',
                },
                size: 'large',
                depthLevel: 5,
                render: () => (
                  <ValidationErrorsModal<SoapState>
                    errors={convertSoapErrorsFromBackend(handledError, flags, isAtendimentoOdonto)}
                    labels={soapFormLabels}
                    config={soapValidationErrorModalConfig}
                    meta={meta}
                  />
                ),
              })()

              // eslint-disable-next-line no-console
              console.error(
                `Erro ao finalizar atendimento ${tipoAtendProf}`,
                JSON.stringify(handleValidationError(firstError))
              )
            }

            throw error
          }
        }
      }
    },
    [
      cidadao.cns,
      cidadao.cpf,
      cidadao.id,
      cidadao.nomeSocial,
      cidadao.nome,
      cidadao.idadeEmAnos,
      handleClickAtualizarCadastroCidadao,
      verificarAgendamentosConflitantes,
      atendimentoId,
      dataNascimentoCidadao,
      dataReferencia,
      tipoAtendProf,
      isAtendimentoObservacao,
      isObservacaoAndResponsavel,
      isAtendimentoOdonto,
      flags,
      isEstagio,
      enviarParaAprovacao,
      analytics,
      nomeCompletoAtendimento,
      alert,
      goToCallbackUrl,
      isRetificacao,
      finalizar,
      printItem,
      isGestante,
      initialValues.antecedentes,
      statusRevisaoAtendimento,
      handleRetificar,
    ]
  )

  const handleSubmitFailed = (formState: FormState<SoapState>) => {
    const { errors, hasValidationErrors } = formState

    if (hasValidationErrors) {
      modal({
        title: 'Para finalizar o atendimento é necessário corrigir o(s) registro(s):',
        icon: {
          name: 'banOutline',
          fill: 'danger',
        },
        size: 'large',
        depthLevel: 5,
        render: () => (
          <ValidationErrorsModal<SoapState>
            errors={errors}
            labels={soapFormLabels}
            config={soapValidationErrorModalConfig}
            meta={meta}
          />
        ),
        onClose: () => {
          analytics.logEvent('erro_finalizar_atend', { erro_atendimento: [JSON.stringify(errors)] })
        },
      })()
    }
  }

  const handleOnFocus = useCallback(() => {
    const activeElement = document.activeElement as HTMLElement
    if (activeElement) {
      const activeElementTop = activeElement.getBoundingClientRect().top
      activeElementTop < 0 && window.scrollTo(0, activeElement.parentElement.offsetTop - 160)
    }
  }, [])

  const renderForm = useCallback(
    (formProps: FormRenderProps<SoapState>): JSX.Element => {
      const { handleSubmit } = formProps

      return (
        <>
          <form
            noValidate
            onKeyDown={handleKeyDown}
            onFocus={handleOnFocus}
            style={{ display: 'flex', flexDirection: 'column', minHeight: '60vh' }}
          >
            <SoapPage
              atendimentoProfissional={atendimentoProfissional}
              dataAtualizacaoObstetricosAntecedentes={
                props.cacheState?.antecedentes?.pessoal?.informacoesObstetricas?.dataAtualizacao
              }
              dataAtualizacaoPuericulturaAntecedentes={
                props.cacheState?.antecedentes?.pessoal?.puericultura?.dataAtualizacao
              }
              cidadao={cidadao}
              dataAtendimento={dataAtendimento}
              prontuarioId={prontuarioId}
              ciapCidPreNatal={ciapCidPreNatal}
              acesso={data.acesso}
              agendamentoAtendimentoId={agendamentoAtendimentoId}
              concluiAgendamento={concluiAgendamento}
              agendamentosDia={agendamentosDia}
              atendimentoId={atendimentoId}
              handleFormChange={handleFormChange}
              profissionalId={data.profissional.id}
              gestacoes={gestacoes}
              problemasAtivosELatentes={problemasAtivosELatentes}
              problemasResolvidos={problemasResolvidos}
              headerRef={headerRef}
              hasProblemaComCiapW78AtivoPersistido={hasProblemaComCiapW78AtivoPersistido}
              hasIvcfPreenchidoEmAtendObservacaoAnterior={hasIvcfPreenchidoEmAtendObservacaoAnterior}
            />
            <AtendimentoIndividualFooter
              nomeAtendimento={nomeAtendimento}
              handleSubmit={handleSubmit}
              handleCancel={handleCancelar}
              cancelButtonLabel={footerMessages.labelButton}
              isLoadingCancelar={isLoadingCancelar}
            />
          </form>
          <SoapRootView
            atendimentoProfissional={atendimentoProfissional}
            cidadao={cidadao}
            dataAtendimento={dataAtendimento}
            isAtendimentoOdonto={isAtendimentoOdonto}
            prontuarioId={prontuarioId}
            ciapCidPreNatal={ciapCidPreNatal}
            somenteCiap={somenteCiap}
            isAtendimentoObservacao={isAtendimentoObservacao}
          />
        </>
      )
    },
    [
      handleOnFocus,
      atendimentoProfissional,
      props.cacheState,
      cidadao,
      dataAtendimento,
      prontuarioId,
      ciapCidPreNatal,
      data.acesso,
      data.profissional.id,
      agendamentoAtendimentoId,
      concluiAgendamento,
      agendamentosDia,
      atendimentoId,
      handleFormChange,
      gestacoes,
      problemasAtivosELatentes,
      problemasResolvidos,
      headerRef,
      hasProblemaComCiapW78AtivoPersistido,
      hasIvcfPreenchidoEmAtendObservacaoAnterior,
      nomeAtendimento,
      handleCancelar,
      footerMessages.labelButton,
      isLoadingCancelar,
      isAtendimentoOdonto,
      somenteCiap,
      isAtendimentoObservacao,
    ]
  )

  const initialDirtyFields = useMemo(
    () =>
      flags.RETIFICACAO_ATENDIMENTO_PROCEDIMENTOS_ENABLED
        ? { values: dirtyInitialValues, names: soapFieldPaths }
        : null,
    [dirtyInitialValues, flags.RETIFICACAO_ATENDIMENTO_PROCEDIMENTOS_ENABLED]
  )

  return (
    <Form<SoapState>
      render={renderForm}
      initialValues={initialValues}
      initialDirtyFields={initialDirtyFields}
      decorators={decorators}
      validateOnBlur
      validate={validators}
      subscription={subscription}
      onSubmit={handleFinalizar}
      onSubmitFailed={handleSubmitFailed}
      mutators={{
        ...arrayMutators,
        changeValue,
      }}
    />
  )
})
